import React from 'react';
import { motion } from 'framer-motion';
import { QrCode, BarChart2, Trophy, Calendar, Wifi, RefreshCw } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { DEMO_URL } from '../constants';

const TV_IMG = '/images/screen-tv-napoli.png';

const featIcons = [QrCode, BarChart2, Trophy, Calendar, Wifi, RefreshCw];

const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.6, ease: 'easeOut', delay },
});

export default function TVScreen({ onDemoClick }) {
  const { t } = useLanguage();
  const tv = t.tvScreen;

  return (
    <section id="pantalla-tv" className="py-14 md:py-20 bg-[#1F2833]" data-testid="tv-screen-section">
      <div className="section-container">

        {/* Row 1: TV image + text side by side */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center mb-14">

          {/* Left: TV mockup */}
          <motion.div className="relative" {...fadeUp()}>
            <div className="absolute inset-0 bg-[#0F6B49]/15 rounded-2xl blur-3xl scale-105" />
            <div className="relative">
              <div className="bg-[#0d1117] rounded-2xl p-3 border border-[#D5A63B]/20 shadow-2xl">
                <div className="bg-[#0d1117] rounded-xl overflow-hidden">
                  <img src={TV_IMG} alt="Pantalla TV MatchPool.live"
                    className="w-full object-cover rounded-xl" data-testid="tv-screen-img" />
                </div>
                <div className="flex justify-center mt-2">
                  <div className="w-16 h-1.5 bg-[#2a3542] rounded-full" />
                </div>
                <div className="flex justify-center">
                  <div className="w-24 h-1 bg-[#2a3542] rounded-full mt-1" />
                </div>
              </div>
              <div className="absolute -top-3 -right-3 bg-red-500 rounded-full px-3 py-1.5 flex items-center gap-1.5
                border border-red-400/50 shadow-lg animate-pulse">
                <div className="w-1.5 h-1.5 rounded-full bg-white" />
                <span className="text-white text-xs font-bold">EN VIVO</span>
              </div>
            </div>
          </motion.div>

          {/* Right: title + body + cta note + button */}
          <motion.div {...fadeUp(0.2)}>
            <div className="divider-gold" />
            <h2 className="heading-lg mb-5">{tv.title}</h2>
            <p className="text-[#8A9099] text-base leading-relaxed mb-8">{tv.body}</p>

            <div className="bg-[#D5A63B]/10 border border-[#D5A63B]/30 rounded-xl p-4 mb-8">
              <p className="text-[#D5A63B] font-bold text-sm font-exo italic">{tv.cta}</p>
            </div>

            <a href={DEMO_URL} target="_blank" rel="noopener noreferrer"
              className="btn-primary no-underline" data-testid="tv-cta-btn">
              {t.nav.requestDemo}
            </a>
          </motion.div>
        </div>

        {/* Row 2: Feature pills — 2 rows × 3 cols, full width */}
        <motion.div
          className="grid grid-cols-2 md:grid-cols-3 gap-3"
          {...fadeUp(0.3)}>
          {tv.features.map((feat, i) => {
            const Icon = featIcons[i] || Wifi;
            return (
              <div key={i}
                className="flex items-center gap-3 bg-[#171E26] rounded-xl px-5 py-3.5
                  border border-[#0F6B49]/20 hover:border-[#D5A63B]/30 transition-colors duration-200"
                data-testid={`tv-feature-${i}`}>
                <Icon size={16} className="text-[#D5A63B] flex-shrink-0" />
                <span className="text-[#F3F1EB] text-sm">{feat}</span>
              </div>
            );
          })}
        </motion.div>

      </div>
    </section>
  );
}
